import { useCallback, useEffect, useRef, useState } from 'react'
import {
  isPageElement,
  isSectionElement,
  isTopLevelFrameElement,
  SketchElement,
  useGoToLayer,
  findSketchElementFromLayerPathHash,
  useCanvasClickHandler,
  useCanvasMouseMoveHandler,
} from '@sketch/inspector'
import { isUserInteractionEnabledOnSketchElement } from './isUserInteractionEnabledOnSketchElement'
import { useQueryParams } from '@sketch/modules-common'
import { useCanvasSelection } from '@sketch/canvas'

/**
 * Create event handler and react states to display hover and selected elements state.
 */
export function useInspectorOverlayEvents(
  sketchSceneRootElement: SketchElement | null,
  /** This should only be enabled when the inspector is open and ready */
  isEnabled: boolean,
  /**
   * @see useInitializeSelectedElement
   */
  autoSelectRootInitially: boolean,
  isTreeFullyLoaded: boolean
) {
  // The selection lives in the shared canvas store, not here: it has to outlive
  // the Inspect tab being open, and the entity overlays write to it in view
  // mode where there is no scene tree to build an element from.
  const { selection, selectElement: setSelectedElement } = useCanvasSelection()

  useInitializeSelectedElement(
    sketchSceneRootElement,
    autoSelectRootInitially && !selection,
    isTreeFullyLoaded
  )

  const [hoveredElement, setHoveredElement] = useState<SketchElement | null>(
    null
  )

  const guardedSetHoveredElement = useCallback(
    (hoveredElement: SketchElement | null) => {
      setHoveredElement(prevHoveredElement => {
        if (hoveredElement?.elementUUID === prevHoveredElement?.elementUUID) {
          // Nothing to update, avoids unnecessary re-renders
          return prevHoveredElement
        }

        if (hoveredElement && isPageElement(hoveredElement)) {
          // We don't allow to hover the page element
          return null
        }

        return hoveredElement
      })
    },
    []
  )

  const { clickHandler, mouseDownHandle } = useCanvasClickHandler(
    isEnabled,
    sketchSceneRootElement,
    setSelectedElement,
    isUserInteractionEnabledOnSketchElement
  )

  const mouseMoveHandler = useCanvasMouseMoveHandler(
    isEnabled,
    sketchSceneRootElement,
    guardedSetHoveredElement,
    isUserInteractionEnabledOnSketchElement
  )

  return {
    hoveredElement,
    setHoveredElement: guardedSetHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  }
}

/**
 * There are two initialization behaviors to handle:
 * - Auto-select the root frame element when in frame view mode
 * - Initialize from URL query param if there is a layer path hash specified
 * The second one has priority over the first one if both conditions are met.
 *
 * Note: On page load, we want to initialize the selected element from the URL query param.
 * However, we don't necessarily have the full sketch scene tree at that time (especially when there are fragments).
 * Therefore we need to retry to initialize the selected element each time the layers tree is updated.
 */
export function useInitializeSelectedElement(
  sketchSceneRootElement: SketchElement | null,
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelectRootFrame: boolean,
  isTreeFullyLoaded: boolean
) {
  /**
   * The layerPath already initialized from, so it happens once per link rather
   * than once per mount. The provider is no longer remounted when the canvas
   * moves to another entity, and following a second layer link would otherwise
   * select nothing.
   */
  const initializedLayerPathRef = useRef<string | null | undefined>(undefined)
  const queryParams = useQueryParams<'FRAME' | 'SHARE_PAGE_CANVAS_VIEW'>()
  const layerPathHashFromUrl = queryParams.layerPath || null
  const goToLayer = useGoToLayer()
  const { selectElement: setSelectedElement } = useCanvasSelection()

  /**
   * In the frame view we want to select the root frame element by default.
   * Note: If there is a layerPathHash in the URL we don't auto select.
   */
  useEffect(() => {
    if (
      !autoSelectRootFrame ||
      !sketchSceneRootElement ||
      (!isTopLevelFrameElement(sketchSceneRootElement) &&
        !isSectionElement(sketchSceneRootElement)) ||
      layerPathHashFromUrl // If there is a layerPathHash in the URL, we don't auto select
    ) {
      return
    }

    setSelectedElement(sketchSceneRootElement)
  }, [
    autoSelectRootFrame,
    layerPathHashFromUrl,
    setSelectedElement,
    sketchSceneRootElement,
  ])

  /**
   * Initialize selected element from URL
   */
  useEffect(() => {
    // There is nothing to initialize to
    if (!layerPathHashFromUrl) {
      initializedLayerPathRef.current = null
      return
    }

    // This link has already been initialized from.
    if (initializedLayerPathRef.current === layerPathHashFromUrl) {
      return
    }

    // Initialize the selected element from the URL param
    const sketchElement = findSketchElementFromLayerPathHash(
      sketchSceneRootElement,
      layerPathHashFromUrl
    )

    if (sketchElement) {
      setSelectedElement(sketchElement)
      goToLayer(sketchElement)
      initializedLayerPathRef.current = layerPathHashFromUrl
      return
    }

    // Else the element is not found but could be loaded later through a frame fragment.
    if (isTreeFullyLoaded) {
      // All fragments have loaded and element still not found - invalid layerPathHash in URL, nothing to select, ignore
      initializedLayerPathRef.current = layerPathHashFromUrl
    }
    // otherwise we wait for fragments to load and try again later
  }, [
    isTreeFullyLoaded,
    layerPathHashFromUrl,
    setSelectedElement,
    sketchSceneRootElement,
    goToLayer,
  ])
}
